/**
 * Composition hook for organization state and actions.
 * Combines TQ queries with useOrganizationActions.
 */

import { useOrganizationStore } from "@/stores/useOrganizationStore";
import {
  useUserOrganizations,
  useOrganizationDetails,
  useOrganizationUsers,
  useOrganizationInvitations,
  useOrganizationTemplates,
} from "@/application/queries";
import { useCurrentOrganization } from "./useCurrentOrganization";
import { useCurrentOrganizationId } from "./useCurrentOrganizationId";
import { useOrganizationActions } from "./useOrganizationActions";
import { AuthContext } from "@/contexts/AuthContext";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useOrgStorageService } from "@/hooks/useRepositories";

export const useOrganizations = () => {
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const orgStorageService = useOrgStorageService();

  const currentOrganization = useCurrentOrganization();
  const orgId = useCurrentOrganizationId();
  const organizationData = useOrganizationStore((s) => s.organizationData);
  const setSelectedOrganizationUuid = useOrganizationStore((s) => s.setSelectedOrganizationUuid);
  const setOrganizationData = useOrganizationStore((s) => s.setOrganizationData);

  // TQ queries
  const userOrganizationsQuery = useUserOrganizations(isAuthenticated);
  const organizationDetailsQuery = useOrganizationDetails(orgId);
  const organizationUsersQuery = useOrganizationUsers(orgId);
  const organizationInvitationsQuery = useOrganizationInvitations(orgId);
  const organizationTemplatesQuery = useOrganizationTemplates(orgId, false);

  const actions = useOrganizationActions();

  // Auto-set org on first load: stored UUID → is_default → first org
  const hasAutoSetRef = useRef(false);
  useEffect(() => {
    if (hasAutoSetRef.current || currentOrganization || !userOrganizationsQuery.data) return;

    const orgs = userOrganizationsQuery.data;
    if (orgs.length === 0) return;

    // 1. Try to restore the last-selected org from localStorage
    const storedUuid = orgStorageService.getCurrentOrgUuid();
    const storedOrg = storedUuid ? orgs.find((o) => o.organization.uuid === storedUuid) : null;

    // 2. Fall back to the default org, then to the first org in the list
    const target = storedOrg ?? orgs.find((o) => o.is_default) ?? orgs[0];
    const targetUuid = target.organization.uuid;

    hasAutoSetRef.current = true;
    setSelectedOrganizationUuid(targetUuid);
    orgStorageService.setCurrentOrgUuid(targetUuid);
  }, [
    userOrganizationsQuery.data,
    currentOrganization,
    setSelectedOrganizationUuid,
    orgStorageService,
  ]);

  const getCurrentOrganizationFormData = useCallback(() => organizationData, [organizationData]);

  return {
    currentOrganization,
    userOrganizations: userOrganizationsQuery.data ?? [],
    organizationDetails: organizationDetailsQuery.data ?? null,
    organizationUsers: organizationUsersQuery.data ?? [],
    organizationInvitations: organizationInvitationsQuery.data ?? [],
    organizationTemplates: organizationTemplatesQuery.data ?? [],
    organizationData,
    error: userOrganizationsQuery.error?.message ?? organizationDetailsQuery.error?.message ?? null,
    isLoadingOrganizations: userOrganizationsQuery.isLoading,
    isLoadingDetails: organizationDetailsQuery.isLoading,
    isLoadingUsers: organizationUsersQuery.isLoading || organizationUsersQuery.isFetching,
    isLoadingInvitations:
      organizationInvitationsQuery.isLoading || organizationInvitationsQuery.isFetching,
    isLoadingTemplates: organizationTemplatesQuery.isLoading,
    isUpdatingOrganization: actions.isUpdatingOrganization,
    isUpdatingMember: false,
    ...actions,
    setOrganizationData,
    getCurrentOrganizationFormData,
  };
};
